/**
 * Cookie Constants
 */
const EXPIRES = ';expires=';
const PATH = ';path=';
const DOMAIN = ';domain=';
const SECURE = ';secure';
const SAMESITE = ';samesite=';

/**
 * Cookie Options
 */
export type CookieOpts = {
    domain?: string;
    path?: string;
    secure?: boolean;
    samesite?: string;
};

/**
 * Cookie map object
 */
export type Cookies = {
    [key: string]: string;
};

/**
 * Cookie class allows us retrieve, update and delete cookies
 *
 * @class
 */
export class Cookie {
    /**
     * Returns the value
     *
     * @param {string} name Cookie's name
     * @returns {string | null}
     */
    static get(name: string): string | null {
        const match = window.document.cookie.match(
            new RegExp(`${name}=([^;]+)`)
        );
        return match ? match[1] : null;
    }

    /**
     * Returns the JSON value
     *
     * @param {string} name Cookie's name
     * @returns {JSON | null}
     */
    static getJSON(name: string): JSON | null {
        const cookie = Cookie.get(name);

        if (!cookie) return null;

        try {
            return JSON.parse(cookie);
        } catch (error) {
            throw new Error(error);
        }
    }

    /**
     * Returns an object with all the cookies
     *
     * @return {Cookies}
     */
    static getAll(): Cookies {
        const cookies: Cookies = {};
        const cookie = window.document.cookie.trim();

        return cookie.split(';').reduce((acc, value) => {
            if (value) {
                const pair = value.split('=');
                acc[pair[0].trim()] = pair[1].trim();
            }
            return acc;
        }, cookies);
    }

    /**
     * This function allows us set a cookie
     *
     * @param {string} name Cookie's name
     * @param {string} value Cookie's value
     * @param {number} days Number of day to calculate the expiration date
     * @param {CookieOpts} opts Optional: Domain, Path, SameSite and Secure
     */
    static set(
        name: string,
        value: string,
        days: number,
        opts: CookieOpts = {}
    ): void {
        const now = new Date();
        const { domain, samesite, secure, path } = opts;
        const isLocalhost = window.document.domain === 'localhost';

        now.setTime(now.getTime() + days * 24 * 60 * 60 * 1000);

        let cookie = name
            .trim()
            .concat('=', value.trim())
            .concat(EXPIRES.concat(now.toUTCString()).trim())
            .concat(PATH.concat(path || '/'));

        if (!isLocalhost) {
            cookie = cookie
                .concat(domain ? DOMAIN.concat(domain) : '')
                .concat(samesite ? SAMESITE.concat(samesite) : '')
                .concat(secure ? SECURE : '');
        }
        window.document.cookie = cookie.trim();
    }

    /**
     * Remove a cookie based on the name
     *
     * @param {string} name
     */
    static remove(name: string): void {
        Cookie.set(name, '', -1);
    }

    /**
     * Remove all the cookies
     */
    static removeAll(): void {
        const cookies = Cookie.getAll();
        Object.keys(cookies).forEach(Cookie.remove);
    }
}
